import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Box,
	CircularProgress,
	Grid,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import { useAcademicYear } from "../../contexts/AcademicYearContext";
import { getAcademicYearRange } from "../../utils/academicYear";
import { ROLES } from "../../config/supabase";
import {
	buildFmePerformanceMetrics,
	buildSchoolProgressBuckets,
	buildServiceScheduleSummary,
	serviceWindowOverlapsAcademicYear,
} from "../../utils/fmePerformanceMetrics";

const cardSx = {
	p: 2.5,
	height: "100%",
	border: "1px solid #e5e7eb",
	borderRadius: "8px",
};

const DashboardCard = ({ title, children, loading }) => (
	<Paper elevation={0} sx={cardSx}>
		<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
			{title}
		</Typography>
		{loading ? (
			<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
				<CircularProgress size={28} />
			</Box>
		) : (
			children
		)}
	</Paper>
);

const ScheduleRow = ({ index, item }) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1.5,
			py: 1,
			borderBottom: "1px solid #f3f4f6",
			"&:last-child": { borderBottom: "none" },
		}}>
		<Typography
			variant="body2"
			sx={{ minWidth: 20, color: "text.secondary", fontWeight: 600 }}>
			{index}.
		</Typography>
		<Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
			{item.visitTypeName}
		</Typography>
		<Typography variant="body2" color="success.main" sx={{ minWidth: 72 }}>
			{item.completed} completed
		</Typography>
		<Typography variant="body2" color="info.main" sx={{ minWidth: 72 }}>
			{item.scheduled} scheduled
		</Typography>
		<Typography variant="body2" color="warning.main" sx={{ minWidth: 96 }}>
			{item.toBeScheduled} to be scheduled
		</Typography>
	</Box>
);

const ProgressBucket = ({ label, count }) => (
	<Box
		sx={{
			display: "flex",
			justifyContent: "space-between",
			alignItems: "center",
			py: 0.75,
			borderBottom: "1px solid #f3f4f6",
			"&:last-child": { borderBottom: "none" },
		}}>
		<Typography variant="body2" color="text.secondary">
			{label}
		</Typography>
		<Typography variant="body2" sx={{ fontWeight: 600 }}>
			{count}
		</Typography>
	</Box>
);

const PerformanceStat = ({ label, value, color = "text.primary" }) => (
	<Box
		sx={{
			display: "flex",
			justifyContent: "space-between",
			alignItems: "center",
			py: 0.75,
		}}>
		<Typography variant="body2" color="text.secondary">
			{label}
		</Typography>
		<Typography variant="body2" sx={{ fontWeight: 600, color }}>
			{value}
		</Typography>
	</Box>
);

const FmeEmployeeDashboard = ({
	supabase,
	user,
	isAdminView = false,
	showFmePerformance = false,
}) => {
	const { currentAcademicYear } = useAcademicYear();
	const [loading, setLoading] = useState(true);
	const [serviceSchedule, setServiceSchedule] = useState([]);
	const [progressBuckets, setProgressBuckets] = useState(null);
	const [performanceSummary, setPerformanceSummary] = useState(null);

	const loadProgressBuckets = async (schools) => {
		const schoolIds = schools.map((school) => school.id).filter(Boolean);

		if (schoolIds.length === 0) {
			setProgressBuckets(buildSchoolProgressBuckets([], [], []));
			return;
		}

		const { data: teachers, error: teachersError } = await supabase
			.from("profiles")
			.select("id, school_id")
			.eq("role", ROLES.SCHOOL_TEACHER)
			.eq("status", "Active")
			.in("school_id", schoolIds);

		if (teachersError) throw teachersError;

		const teacherIds = (teachers || []).map((teacher) => teacher.id);
		let courseProgress = [];

		if (teacherIds.length > 0) {
			const { data: progressData, error: progressError } = await supabase
				.from("teacher_course_progress")
				.select("teacher_id, completion_percentage")
				.in("teacher_id", teacherIds)
				.eq("academic_year", currentAcademicYear);

			if (progressError) throw progressError;
			courseProgress = progressData || [];
		}

		setProgressBuckets(
			buildSchoolProgressBuckets(schools, teachers || [], courseProgress)
		);
	};

	const fetchDashboardData = useCallback(async () => {
		try {
			setLoading(true);

			const academicYearRange = getAcademicYearRange(currentAcademicYear);

			const [
				{ data: visitTypes, error: visitTypesError },
				{ data: visits, error: visitsError },
			] = await Promise.all([
				supabase
					.from("visit_types")
					.select("*")
					.eq("status", true)
					.eq("visit_type", "Service")
					.not("start_date", "is", null)
					.not("end_date", "is", null)
					.order("start_date"),
				supabase
					.from("school_visits")
					.select(
						`
						id,
						school_id,
						visit_type_id,
						visited_by,
						planned_by,
						visited_date,
						planned_date
					`
					)
					.not("visit_type_id", "is", null),
			]);

			if (visitTypesError) throw visitTypesError;
			if (visitsError) throw visitsError;

			const windowsForYear = (visitTypes || []).filter((window) =>
				serviceWindowOverlapsAcademicYear(window, academicYearRange)
			);

			if (isAdminView) {
				const [
					{ data: employees, error: employeesError },
					{ data: assignments, error: assignmentsError },
					{ data: schools, error: schoolsError },
				] = await Promise.all([
					supabase
						.from("profiles")
						.select("id, full_name, email, status")
						.eq("role", ROLES.FUNMIYO_EMPLOYEE)
						.eq("status", "Active"),
					supabase
						.from("employee_schools")
						.select(
							`
							profile_id,
							school_id,
							schools:school_id (
								id,
								name,
								district,
								status
							)
						`
						)
						.eq("active_status", true),
					supabase
						.from("schools")
						.select("id, name, district, status")
						.eq("status", "Active")
						.order("name"),
				]);

				if (employeesError) throw employeesError;
				if (assignmentsError) throw assignmentsError;
				if (schoolsError) throw schoolsError;

				const activeAssignments = (assignments || []).filter(
					(row) => row.schools && row.schools.status === "Active"
				);

				const metrics = buildFmePerformanceMetrics({
					employees: employees || [],
					assignments: activeAssignments,
					serviceWindows: windowsForYear,
					visits: visits || [],
				});

				setServiceSchedule(buildServiceScheduleSummary(metrics.rows));
				setPerformanceSummary(
					showFmePerformance && metrics.totals.expectedVisits > 0
						? metrics.totals
						: null
				);
				await loadProgressBuckets(schools || []);
				return;
			}

			const [
				{ data: profile, error: profileError },
				{ data: assignments, error: assignmentsError },
			] = await Promise.all([
				supabase
					.from("profiles")
					.select("id, full_name, email, status")
					.eq("id", user.id)
					.single(),
				supabase
					.from("employee_schools")
					.select(
						`
						profile_id,
						school_id,
						schools:school_id (
							id,
							name,
							district,
							status
						)
					`
					)
					.eq("profile_id", user.id)
					.eq("active_status", true),
			]);

			if (profileError) throw profileError;
			if (assignmentsError) throw assignmentsError;

			const activeAssignments = (assignments || []).filter(
				(row) => row.schools && row.schools.status === "Active"
			);
			const employeeVisits = (visits || []).filter(
				(visit) =>
					visit.planned_by === user.id || visit.visited_by === user.id
			);

			const metrics = buildFmePerformanceMetrics({
				employees: [profile],
				assignments: activeAssignments,
				serviceWindows: windowsForYear,
				visits: employeeVisits,
			});

			setServiceSchedule(
				buildServiceScheduleSummary(
					metrics.rows.filter((row) => row.fmeId === user.id)
				)
			);
			setPerformanceSummary(null);

			const schools = activeAssignments.map((row) => row.schools);
			await loadProgressBuckets(schools);
		} catch (err) {
			console.error("Error loading FME dashboard:", err);
			setServiceSchedule([]);
			setProgressBuckets(buildSchoolProgressBuckets([], [], []));
			setPerformanceSummary(null);
		} finally {
			setLoading(false);
		}
	}, [
		supabase,
		user.id,
		currentAcademicYear,
		isAdminView,
		showFmePerformance,
	]);

	useEffect(() => {
		fetchDashboardData();
	}, [fetchDashboardData]);

	const progressRows = useMemo(
		() => [
			{ key: "exact100", label: "100%" },
			{ key: "above90", label: ">90" },
			{ key: "above80", label: ">80" },
			{ key: "above70", label: ">70" },
			{ key: "below60", label: "<60" },
		],
		[]
	);

	return (
		<Box sx={{ mt: 1 }}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={4}>
					<DashboardCard title="Service schedule" loading={loading}>
						{serviceSchedule.length === 0 ? (
							<Typography variant="body2" color="text.secondary">
								No service windows configured for this academic year.
							</Typography>
						) : (
							<Stack spacing={0}>
								{serviceSchedule.map((item, index) => (
									<ScheduleRow key={item.visitTypeId} index={index + 1} item={item} />
								))}
							</Stack>
						)}
					</DashboardCard>
				</Grid>

				<Grid item xs={12} md={4}>
					<DashboardCard title="Progress Report" loading={loading}>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
							No. of schools — {progressBuckets?.totalSchools ?? 0}{" "}
							{isAdminView ? "active" : "assigned"}
						</Typography>
						{progressRows.map((row) => (
							<ProgressBucket
								key={row.key}
								label={row.label}
								count={progressBuckets?.[row.key] ?? 0}
							/>
						))}
					</DashboardCard>
				</Grid>

				{showFmePerformance && (
					<Grid item xs={12} md={4}>
						<DashboardCard title="FME performance" loading={loading}>
							{!performanceSummary || performanceSummary.expectedVisits === 0 ? (
								<Typography variant="body2" color="text.secondary">
									No service visit data available yet.
								</Typography>
							) : (
								<Stack spacing={0.5}>
									{isAdminView && (
										<PerformanceStat
											label="Active experts"
											value={performanceSummary.fmeCount ?? 0}
										/>
									)}
									<PerformanceStat
										label="Compliance rate"
										value={`${performanceSummary.complianceRate}%`}
										color="primary.main"
									/>
									<PerformanceStat
										label="Completed on time"
										value={performanceSummary.completedOnTime}
										color="success.main"
									/>
									<PerformanceStat
										label="Early"
										value={performanceSummary.early}
										color="info.main"
									/>
									<PerformanceStat
										label="Late"
										value={performanceSummary.late}
										color="warning.main"
									/>
									<PerformanceStat
										label="Missed"
										value={performanceSummary.missed}
										color="error.main"
									/>
									<PerformanceStat
										label="Pending / upcoming"
										value={
											performanceSummary.pending + performanceSummary.upcoming
										}
									/>
								</Stack>
							)}
						</DashboardCard>
					</Grid>
				)}

				<Grid item xs={12} md={4}>
					<DashboardCard title="Team Meetings" loading={loading}>
						<Typography variant="body2" color="text.secondary">
							No team meetings scheduled.
						</Typography>
					</DashboardCard>
				</Grid>
			</Grid>
		</Box>
	);
};

export default FmeEmployeeDashboard;
